import React, { useMemo, useState, useCallback } from 'react';
import {
  Avatar,
  Box,
  Skeleton,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Collapse,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { buildSummary, getUserName, processEntries } from './historyUtils';

/* ================= USER AVATAR ================= */

const UserAvatar = ({ user, size = 32 }) => {
  const initials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`
    : '?';

  return (
    <Avatar sx={{ width: size, height: size }}>{initials.toUpperCase()}</Avatar>
  );
};


const getRelativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ================= DIFF ================= */

const DiffText = ({ oldVal, newVal }) => {
  if (!oldVal && newVal) {
    return (
      <Typography component="span" sx={{ color: 'success.dark' }}>
        {newVal}
      </Typography>
    );
  }

  if (oldVal && !newVal) {
    return (
      <Typography component="span" sx={{ color: 'error.dark' }}>
        removed ({oldVal})
      </Typography>
    );
  }

  if (oldVal && newVal) {
    return (
      <>
        <Typography component="span" sx={{ color: 'text.secondary' }}>
          {oldVal}
        </Typography>
        {' → '}
        <Typography component="span" sx={{ color: 'success.dark' }}>
          {newVal}
        </Typography>
      </>
    );
  }

  return null;
};

/* ================= COMPONENT ================= */

const HistoryFeed = ({ entries = [], isLoading }) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [order, setOrder] = useState('desc');

  const toggle = useCallback((id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const data = useMemo(() => {
    let processed = processEntries(entries);

    const q = search.toLowerCase();

    if (q) {
      processed = processed.filter((e) => {
        const user = getUserName(e.performed_by_user).toLowerCase();
        const action = (e.action_type || '').toLowerCase();
        const summary = buildSummary(e).toLowerCase();

        const changesText = (e.changes || [])
          .flatMap((c) => [c.field, c.old, c.new])
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return (
          user.includes(q) ||
          action.includes(q) ||
          summary.includes(q) ||
          changesText.includes(q)
        );
      });
    }

    processed.sort((a, b) =>
      order === 'desc'
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at),
    );

    return processed;
  }, [entries, search, order]);

  if (isLoading) {
    return (
      <Box p={2}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box p={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            size="small"
            value={order}
            exclusive
            onChange={(e, val) => val && setOrder(val)}>
            <ToggleButton value="desc">Newest</ToggleButton>
            <ToggleButton value="asc">Oldest</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {data.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography color="text.secondary">No activity yet</Typography>
        </Box>
      ) : (
        data.map((entry) => {
          const isCreate = entry.action_type === 'CREATE';
          const isSingle = entry.changes.length === 1;
          const isExpandable = !isCreate && entry.changes.length > 1;
          const isOpen = isSingle || expanded[entry.id];

          return (
            <Box
              key={entry.id}
              px={2}
              py={1.5}
              borderBottom="1px solid"
              borderColor="divider">
              <Stack direction="row" spacing={2}>
                <UserAvatar user={entry.performed_by_user} />

                <Box flex={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      <strong>{getUserName(entry.performed_by_user)}</strong>{' '}
                      {buildSummary(entry)}
                    </Typography>

                    {isExpandable && (
                      <IconButton size="small" onClick={() => toggle(entry.id)}>
                        <ExpandMoreIcon
                          sx={{
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                          }}
                        />
                      </IconButton>
                    )}
                  </Stack>

                  {isCreate ? (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Created this test case
                    </Typography>
                  ) : isSingle ? (
                    entry.changes.length > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}>
                        {entry.changes[0].field}:{' '}
                        <DiffText
                          oldVal={entry.changes[0].old}
                          newVal={entry.changes[0].new}
                        />
                      </Typography>
                    )
                  ) : (
                    <Collapse in={isOpen}>
                      <Box mt={1}>
                        {entry.changes.map((c, i) => (
                          <Box
                            key={i}
                            display="grid"
                            gridTemplateColumns="160px 1fr"
                            gap={1}
                            py={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              {c.field}
                            </Typography>

                            <Typography variant="body2">
                              <DiffText oldVal={c.old} newVal={c.new} />
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {getRelativeTime(entry.created_at)}
                </Typography>
              </Stack>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default HistoryFeed;